import { useState, useEffect } from 'react';

import { ShiftStats } from '../services/api';

import ShiftAlerts from './ShiftAlerts';

const Home = () => {
  const [currentSlideIndex, setCurrentSlideIndex] = useState(0);
  // Hardcoded shift stats
  const [shiftStats] = useState<ShiftStats>({
    totalShifts: 6,
    avgPayRate: 34,
    responseTime: '2.3 min'
  });
  // Remove setShiftStats and setLoading

  useEffect(() => {
    // Auto-slide functionality
    const interval = setInterval(() => {
      setCurrentSlideIndex((prev) => (prev === 0 ? 1 : 0));
    }, 5000);
    return () => clearInterval(interval);
  }, []);

  const currentSlide = (index: number) => {
    setCurrentSlideIndex(index);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-card1 to-white">
      {/* Hero Section */}
      <section className="relative bg-hero-pattern text-white py-24 lg:py-32 overflow-hidden">
        {/* Background Elements */}
        <div className="absolute inset-0 bg-gradient-to-br from-primary/90 to-primaryDark/90"></div>
        <div className="absolute top-0 left-0 w-full h-full">
          <div className="absolute top-20 left-10 w-32 h-32 bg-secondary/10 rounded-full animate-float"></div>
          <div className="absolute top-40 right-20 w-24 h-24 bg-blue500/10 rounded-full animate-float" style={{ animationDelay: '2s' }}></div>
          <div className="absolute bottom-20 left-1/4 w-16 h-16 bg-green600/10 rounded-full animate-float" style={{ animationDelay: '4s' }}></div>
        </div>
        
        <div className="relative max-w-7xl mx-auto px-6 lg:px-8">
          <div className="text-center animate-fade-in">
            <h1 className="text-5xl md:text-7xl font-bold mb-8 leading-tight">
              Welcome to{' '}
              <span className="bg-gradient-to-r from-secondary via-yellow100 to-secondaryDark bg-clip-text text-transparent animate-pulse-slow">
                VitalHive
              </span>
            </h1>
            <p className="text-xl md:text-2xl text-primaryLight mb-12 max-w-4xl mx-auto leading-relaxed">
              Driving Innovation in Healthcare Staffing with cutting-edge technology and seamless solutions.
            </p>
          </div>
        </div>
      </section>

      {/* Slideshow Section */}
      <section className="py-20 bg-gradient-to-br from-card1 to-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-primary mb-6">
              Our Solutions
            </h2>
            <p className="text-xl text-primaryLight max-w-3xl mx-auto">
              Discover how VitalHive transforms healthcare staffing with innovative technology
            </p>
          </div>
          
          <div className="relative">
            <div className="relative h-96 lg:h-[500px] rounded-3xl overflow-hidden shadow-strong">
              <div className={`absolute inset-0 transition-all duration-1000 ${currentSlideIndex === 0 ? 'opacity-100 scale-100' : 'opacity-0 scale-105'}`}>
                <img 
                  src="/assets/images/home-img1.png" 
                  alt="Healthcare Staffing Solution" 
                  className="w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-primary/80 via-primary/40 to-transparent flex items-end">
                  <div className="p-8 lg:p-12 text-white">
                    <h3 className="text-3xl lg:text-4xl font-bold mb-4">Smart Staffing Management</h3>
                    <p className="text-lg lg:text-xl text-primaryLight">Organized scheduling for healthcare professionals with AI-powered optimization</p>
                  </div>
                </div>
              </div>
              <div className={`absolute inset-0 transition-all duration-1000 ${currentSlideIndex === 1 ? 'opacity-100 scale-100' : 'opacity-0 scale-105'}`}>
                <img 
                  src="/assets/images/home-img2.png" 
                  alt="Automated Billing" 
                  className="w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-primary/80 via-primary/40 to-transparent flex items-end">
                  <div className="p-8 lg:p-12 text-white">
                    <h3 className="text-3xl lg:text-4xl font-bold mb-4">Automated Billing & Compliance</h3>
                    <p className="text-lg lg:text-xl text-primaryLight">Streamlined invoicing and credential verification with real-time tracking</p>
                  </div>
                </div>
              </div>
            </div>
            
            {/* Navigation dots */}
            <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 flex space-x-4">
              <button 
                onClick={() => currentSlide(0)}
                className={`w-4 h-4 rounded-full transition-all duration-300 ${currentSlideIndex === 0 ? 'bg-secondary shadow-glow' : 'bg-white/50 hover:bg-white/75'}`}
              ></button>
              <button 
                onClick={() => currentSlide(1)}
                className={`w-4 h-4 rounded-full transition-all duration-300 ${currentSlideIndex === 1 ? 'bg-secondary shadow-glow' : 'bg-white/50 hover:bg-white/75'}`}
              ></button>
            </div>
          </div>
        </div>
      </section>

      {/* Stats Bar */}
      <section className="py-20 bg-gradient-to-r from-secondaryLight via-orange50 to-yellow100">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="text-center group">
              <div className="bg-white rounded-2xl p-8 shadow-soft hover:shadow-medium transition-all duration-300 transform hover:-translate-y-2">
                <div className="w-16 h-16 bg-gradient-to-br from-blue500 to-blue700 rounded-2xl flex items-center justify-center mx-auto mb-6">
                  <i className="fas fa-calendar-check text-2xl text-white"></i>
                </div>
                <div className="text-4xl font-bold text-primary mb-2">
                  {shiftStats.totalShifts}
                </div>
                <div className="text-lg text-primaryLight font-semibold">Active Shifts</div>
              </div>
            </div>
            <div className="text-center group">
              <div className="bg-white rounded-2xl p-8 shadow-soft hover:shadow-medium transition-all duration-300 transform hover:-translate-y-2">
                <div className="w-16 h-16 bg-gradient-to-br from-green600 to-green800 rounded-2xl flex items-center justify-center mx-auto mb-6">
                  <i className="fas fa-dollar-sign text-2xl text-white"></i>
                </div>
                <div className="text-4xl font-bold text-primary mb-2">
                  ${shiftStats.avgPayRate}
                </div>
                <div className="text-lg text-primaryLight font-semibold">Avg Pay Rate</div>
              </div>
            </div>
            <div className="text-center group">
              <div className="bg-white rounded-2xl p-8 shadow-soft hover:shadow-medium transition-all duration-300 transform hover:-translate-y-2">
                <div className="w-16 h-16 bg-gradient-to-br from-orange500 to-orange700 rounded-2xl flex items-center justify-center mx-auto mb-6">
                  <i className="fas fa-clock text-2xl text-white"></i>
                </div>
                <div className="text-4xl font-bold text-primary mb-2">
                  {shiftStats.responseTime}
                </div>
                <div className="text-lg text-primaryLight font-semibold">Avg Response Time</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Shift Alerts Section */}
      <ShiftAlerts />

      {/* Features Section */}
      <section className="py-20 bg-gradient-to-br from-card1 to-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-primary mb-6">
              Why Choose VitalHive?
            </h2>
            <p className="text-xl text-primaryLight max-w-3xl mx-auto">
              Experience the future of healthcare staffing with our comprehensive solutions
            </p>
          </div>
          
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            <div className="bg-card-gradient p-8 lg:p-12 rounded-3xl shadow-soft hover:shadow-medium transition-all duration-300 transform hover:-translate-y-2">
              <div className="w-16 h-16 bg-gradient-to-br from-blue500 to-blue700 rounded-2xl flex items-center justify-center mb-8">
                <i className="fas fa-rocket text-2xl text-white"></i>
              </div>
              <h3 className="text-3xl font-bold text-primary mb-8">Enterprise-Ready Solutions</h3>
              <ul className="space-y-6 text-primaryLight">
                <li className="flex items-start space-x-4">
                  <div className="w-8 h-8 bg-green100 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                    <i className="fas fa-check text-green600 text-sm"></i>
                  </div>
                  <span className="text-lg">Tailored for healthcare systems of all sizes</span>
                </li>
                <li className="flex items-start space-x-4">
                  <div className="w-8 h-8 bg-green100 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                    <i className="fas fa-check text-green600 text-sm"></i>
                  </div>
                  <span className="text-lg">Seamless staff scheduling and shift management</span>
                </li>
                <li className="flex items-start space-x-4">
                  <div className="w-8 h-8 bg-green100 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                    <i className="fas fa-check text-green600 text-sm"></i>
                  </div>
                  <span className="text-lg">Scalable integrations with hospital infrastructure</span>
                </li>
                <li className="flex items-start space-x-4">
                  <div className="w-8 h-8 bg-green100 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                    <i className="fas fa-check text-green600 text-sm"></i>
                  </div>
                  <span className="text-lg">Automated billing, credential tracking, and time management</span>
                </li>
              </ul>
            </div>
            
            <div className="bg-gradient-to-br from-secondaryLight to-orange50 p-8 lg:p-12 rounded-3xl shadow-soft hover:shadow-medium transition-all duration-300 transform hover:-translate-y-2">
              <div className="w-16 h-16 bg-gradient-to-br from-secondary to-secondaryDark rounded-2xl flex items-center justify-center mb-8">
                <i className="fas fa-heartbeat text-2xl text-white"></i>
              </div>
              <h3 className="text-3xl font-bold text-primary mb-8">VitalHive Healthcare Staffing</h3>
              <p className="text-lg text-primaryLight mb-8 leading-relaxed">
                VitalHive is an all-in-one staffing platform designed for healthcare institutions and professionals, providing comprehensive solutions for modern healthcare challenges.
              </p>
              <ul className="space-y-6 text-primaryLight">
                <li className="flex items-start space-x-4">
                  <div className="w-8 h-8 bg-orange100 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                    <i className="fas fa-check text-orange700 text-sm"></i>
                  </div>
                  <span className="text-lg">Track shifts in real time with live updates</span>
                </li>
                <li className="flex items-start space-x-4">
                  <div className="w-8 h-8 bg-orange100 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                    <i className="fas fa-check text-orange700 text-sm"></i>
                  </div>
                  <span className="text-lg">Automate invoicing & time tracking</span>
                </li>
                <li className="flex items-start space-x-4">
                  <div className="w-8 h-8 bg-orange100 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                    <i className="fas fa-check text-orange700 text-sm"></i>
                  </div>
                  <span className="text-lg">Enable multi-facility access</span>
                </li>
                <li className="flex items-start space-x-4">
                  <div className="w-8 h-8 bg-orange100 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                    <i className="fas fa-check text-orange700 text-sm"></i>
                  </div>
                  <span className="text-lg">Simplify compliance with built-in credential verification</span>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Perdium Section */}
      <section className="py-20 bg-gradient-to-br from-orange100 via-secondaryLight to-secondary text-primaryDark relative overflow-hidden">
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute top-10 right-10 w-32 h-32 bg-orange100/40 rounded-full animate-float"></div>
          <div className="absolute bottom-10 left-10 w-24 h-24 bg-yellow100/40 rounded-full animate-float" style={{ animationDelay: '3s' }}></div>
        </div>
        <div className="relative max-w-4xl mx-auto px-6 lg:px-8 text-center">
          <div className="w-20 h-20 bg-secondaryLight/60 rounded-3xl flex items-center justify-center mx-auto mb-8">
            <i className="fas fa-bolt text-3xl text-secondaryDark"></i>
          </div>
          <h1 className="text-4xl md:text-6xl font-bold mb-8 text-primaryDark">
            Get Instant Pay
          </h1>
          <p className="text-xl md:text-2xl mb-12 text-primaryDark/80 leading-relaxed">
            Get your full paycheck deposited instantly at any day of the week. No fees, no delays. Taxes handled seamlessly with our advanced payment system.
          </p>
          <button className="bg-white text-secondaryDark font-bold px-10 py-4 rounded-2xl shadow-strong hover:shadow-glow transition-all duration-300 transform hover:scale-105">
            Wanna know how?
          </button>
        </div>
      </section>
    </div>
  );
};

export default Home; 